/**
 * Synchronisation de la session FlowDino vers l'extension Chrome.
 *
 * L'extension déclare `externally_connectable` pour flowdino.com / flowdino.fr /
 * localhost : `chrome.runtime.sendMessage(extensionId, ...)` est donc injecté par le
 * navigateur sur ces domaines, sans librairie à installer.
 *
 * Voir docs/connexion-auto-extension.md pour le contrat de messages complet.
 */

// Extension publiée sur le Chrome Web Store.
const PRODUCTION_EXTENSION_ID = 'iealfioknccembacfpjgodhoehdmggak';

// Extension chargée non empaquetée : n'importe qui peut la charger localement, on ne
// doit donc jamais lui confier la session en production.
const DEVELOPMENT_EXTENSION_ID = 'goaldpemijgjhldalijmljhahjepiifj';

const isDevelopment = (process.env.REACT_APP_ENV || 'development') === 'development';

// Comptes autorisés à replier sur l'extension de développement en production.
// Voir {@link EXTENSION_IDS} pour la raison d'être de cette liste.
const DEVELOPMENT_EXTENSION_TESTERS = ['user@example.com'];

/**
 * Indique si la session peut être confiée à l'extension de développement.
 *
 * Hors production, toujours. En production, seulement pour les comptes de
 * {@link DEVELOPMENT_EXTENSION_TESTERS} : l'extension de développement est
 * chargée non empaquetée, donc n'importe qui peut charger une extension portant
 * cet identifiant. La lui confier pour tous reviendrait à remettre le JWT et le
 * refresh token de chaque utilisateur à une extension arbitraire.
 */
const allowsDevelopmentExtension = (candidateEmail = null) => {
    if (isDevelopment) return true;

    // L'email de la session en cours de synchronisation prime sur le localStorage :
    // l'appelant le connaît parfois avant qu'il n'y soit écrit (connexion), ou
    // quand il n'y est plus (rafraîchissement après purge partielle).
    const email = candidateEmail || readStoredEmail();
    return Boolean(email) && DEVELOPMENT_EXTENSION_TESTERS.includes(email.trim().toLowerCase());
};

/**
 * Extensions à qui l'on confie **la session** (`AUTH_SYNC`), par ordre de
 * préférence : la production d'abord, la développement en repli.
 *
 * C'est une fonction et non une constante : l'autorisation dépend du compte
 * connecté, qui change au cours de la vie de l'onglet (connexion, déconnexion,
 * changement de compte). Figée au chargement du module, elle refléterait l'état
 * du localStorage au premier import.
 *
 * @returns {string[]}
 */
export const getAuthExtensionIds = (candidateEmail = null) => (
    allowsDevelopmentExtension(candidateEmail)
        ? [PRODUCTION_EXTENSION_ID, DEVELOPMENT_EXTENSION_ID]
        : [PRODUCTION_EXTENSION_ID]);

/**
 * @deprecated Utiliser {@link getAuthExtensionIds}, qui tient compte du compte
 * connecté. Conservé pour les appelants qui n'ont besoin que de la liste de base.
 */
export const EXTENSION_IDS = isDevelopment
    ? [PRODUCTION_EXTENSION_ID, DEVELOPMENT_EXTENSION_ID]
    : [PRODUCTION_EXTENSION_ID];

/**
 * Extensions autorisées à **publier** un lot d'articles.
 *
 * L'extension de développement est incluse même en production : c'est ce qui
 * permet de tester la publication sur flowdino.com sans build empaqueté.
 *
 * Le risque n'est pas le même que pour {@link EXTENSION_IDS} : un lot ne
 * transporte aucun secret, seulement des articles que l'utilisateur possède
 * déjà, et c'est lui qui déclenche l'envoi. Une extension pirate portant cet
 * identifiant n'apprendrait rien qu'elle ne puisse déjà lire sur la page.
 *
 * À restreindre à la seule extension de production le jour où la publication
 * sortira de la phase de mise au point (cf. docs/publication-multisite-implementation.md).
 */
export const PUBLISH_EXTENSION_IDS = [PRODUCTION_EXTENSION_ID, DEVELOPMENT_EXTENSION_ID];

// Délai au-delà duquel on considère que l'extension ne répondra pas. Chrome appelle
// normalement le callback, mais une extension en cours de mise à jour peut rester muette.
const RESPONSE_TIMEOUT_MS = 2000;

const isChromeMessagingAvailable = () =>
    typeof chrome !== 'undefined' && !!chrome.runtime && !!chrome.runtime.sendMessage;

// Incrémenté à chaque déconnexion. Une synchro entamée avant une déconnexion et
// terminée après doit être abandonnée plutôt que de réinstaller la session.
let logoutEpoch = 0;

/**
 * Envoie un message à une extension et résout avec sa réponse, ou `null` si
 * l'extension est absente, muette ou en erreur.
 *
 * `chrome.runtime.lastError` doit être lu dans le callback même quand on ignore le
 * cas, sinon Chrome journalise une erreur non interceptée dans la console.
 */
const sendMessage = (extensionId, message) =>
    new Promise((resolve) => {
        let settled = false;

        const settle = (value) => {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            resolve(value);
        };

        const timeoutId = setTimeout(() => settle(null), RESPONSE_TIMEOUT_MS);

        try {
            chrome.runtime.sendMessage(extensionId, message, (response) => {
                if (chrome.runtime.lastError) {
                    // Extension non installée ou non joignable : cas nominal, pas une erreur.
                    settle(null);
                    return;
                }
                settle(response || null);
            });
        } catch (error) {
            settle(null);
        }
    });

/**
 * Liste les extensions FlowDino réellement installées.
 *
 * @param {string|null} [candidateEmail] Email de la session en cours de
 *        synchronisation, qui décide de l'accès à l'extension de développement
 *        (cf. {@link getAuthExtensionIds}). Omis, c'est le compte du localStorage.
 * @returns {Promise<Array<{ extensionId: string, version: string|undefined }>>}
 */
export const pingExtensions = async (candidateEmail = null) => {
    if (!isChromeMessagingAvailable()) return [];

    const results = await Promise.all(
        getAuthExtensionIds(candidateEmail).map(async (extensionId) => {
            const pong = await sendMessage(extensionId, { type: 'PING' });
            if (!pong || !pong.installed) return null;
            return { extensionId, version: pong.version };
        })
    );

    return results.filter(Boolean);
};

/**
 * Indique si au moins une extension FlowDino est installée.
 * @returns {Promise<boolean>}
 */
export const isExtensionInstalled = async () => (await pingExtensions()).length > 0;

/**
 * Pousse la session courante vers les extensions installées.
 *
 * Le couple transmis est exactement celui renvoyé par `POST /api/login_check`. Le
 * `refresh_token` est indispensable : sans lui, l'extension cesse de fonctionner une
 * heure après la synchronisation.
 *
 * @param {{ token: string, refreshToken?: string|null, email?: string|null }} [session]
 *        Session à pousser. Par défaut, celle stockée dans le localStorage.
 * @returns {Promise<{ synced: number, installed: number, errors: string[] }>}
 */
export const syncSessionToExtension = async (session = null) => {
    const empty = { synced: 0, installed: 0, errors: [] };

    // Tout est lu avant le moindre await : le localStorage peut être vidé par une
    // déconnexion pendant que le PING est en vol.
    const token = (session && session.token) || localStorage.getItem('token');
    if (!token) return empty;

    // Chaque champ non fourni retombe sur le localStorage : un appelant qui ne connaît
    // que le token (le refresh, par exemple) n'a pas à réunir le reste lui-même.
    const refreshToken = (session && session.refreshToken) || localStorage.getItem('refresh_token');
    const email = (session && session.email) || readStoredEmail();
    const epochAtStart = logoutEpoch;

    const installed = await pingExtensions(email);
    if (!installed.length) return empty;

    // Une déconnexion survenue pendant le PING l'emporte : ne pas réinstaller une
    // session dans une extension que l'utilisateur vient de purger.
    if (logoutEpoch !== epochAtStart) return empty;

    const errors = [];
    let synced = 0;

    await Promise.all(
        installed.map(async ({ extensionId }) => {
            const response = await sendMessage(extensionId, {
                type: 'AUTH_SYNC',
                token,
                refresh_token: refreshToken || undefined,
                email: email || undefined
            });

            if (response && response.success) {
                synced += 1;
            } else if (response && response.error) {
                errors.push(response.error);
            } else {
                errors.push('NO_RESPONSE');
            }
        })
    );

    return { synced, installed: installed.length, errors };
};

/**
 * Purge la session stockée dans les extensions installées.
 * À appeler à la déconnexion du site, avant toute redirection.
 *
 * @returns {Promise<void>}
 */
export const logoutExtension = async () => {
    // Invalide toute synchro en vol, y compris si le messaging n'est pas disponible.
    logoutEpoch += 1;

    if (!isChromeMessagingAvailable()) return;

    // Toutes les extensions connues, sans filtrer sur le compte : la purge doit
    // atteindre une session posée par un compte testeur même si l'utilisateur
    // courant n'en est pas un, sinon elle survivrait à la déconnexion.
    await Promise.all(
        [PRODUCTION_EXTENSION_ID, DEVELOPMENT_EXTENSION_ID].map(
            (extensionId) => sendMessage(extensionId, { type: 'AUTH_LOGOUT' })
        )
    );
};

/**
 * Variante « tire et oublie » de {@link logoutExtension}, pour les chemins de
 * déconnexion synchrones qui ne peuvent pas attendre la réponse de l'extension.
 */
export const logoutExtensionSilently = () => {
    logoutExtension().catch(() => {});
};

/**
 * Variante « tire et oublie » de {@link syncSessionToExtension}.
 * Une synchro qui échoue ne doit jamais bloquer une connexion réussie au site.
 */
export const syncSessionToExtensionSilently = (session = null) => {
    syncSessionToExtension(session).catch(() => {});
};

const readStoredEmail = () => {
    const directEmail = localStorage.getItem('userEmail');
    if (directEmail) return directEmail;

    try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null');
        return userInfo && userInfo.email ? userInfo.email : null;
    } catch (error) {
        return null;
    }
};
